import React, { useRef, useState, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import classNames from 'classnames'

import { SiteState } from '../store/site-reducer'
import { GraphQLState } from '../store/graphql-reducer'
import ChangeInput from './change-input'
import Row, { Slot } from './row'
import styles from '../component-styles/settings-v2.module.scss'
import {
  updateUserProfile,
  updateUserEmail,
  updateUserName,
} from '../api-client/user-client'
import { updatePassword } from '../api-client/support-client'
import { updateUserImage } from '../api-client/graphql-client'
import { validatePassword, validateEmail } from '../helpers/forms'
import {
  getSelectedAccountAsync,
  getUserAccountDetailsGQL,
  getUserListAsync,
  updatePreferredHomepage,
  updateMarketingPermissionGQL,
  logActionAsync,
  getCampaignLinkDashboardMetaDefaultsGQL,
} from '../store/actions'
import useAPI from '../hooks/useAPI'
import {
  getEmailNotifications,
  toggleEmailNotifications,
} from '../api-client/settings-client'
import useOnboarding from '../hooks/useOnboarding'
import Input, { Label } from './input'
import icons from '../theme/icons'
import colors from '../theme/colors'
import { ImgUploader } from './img-uploader'

interface PreferencesProps {
  showValue?: string | null
  className?: string
}

export function Preferences({ showValue, className }: PreferencesProps) {
  const notificationsRef = useRef<HTMLSpanElement>(null)
  const [checkedHomepage, setCheckedHomepage] = useState('welcome')
  const [acceptedMarketing, setAcceptedMarketing] = useState(false)
  const [availableModules, setAvailableModules] = useState({
    audit: false,
    explain: false,
    report: false,
    track: false,
  })
  const [homepageError, setHomepageError] = useState(false)
  const [emailSettings, refetch] = useAPI(getEmailNotifications)

  const {
    site: { selected },
    graphql: { currentUser, currentAccount, error },
  } = useSelector(
    ({ site, graphql }: { site: SiteState; graphql: GraphQLState }) => ({
      site,
      graphql,
    }),
  )
  const dispatch = useDispatch()

  const {
    email,
    firstName,
    lastName,
    userName,
    publicJobTitle,
    currentAccountPermission,
  } = currentUser || {}
  const accountID = currentAccount?.accountID || ''

  useEffect(() => {
    dispatch(getCampaignLinkDashboardMetaDefaultsGQL())
  }, [performance])

  useEffect(() => {
    if (
      // Autoscroll to notifications.
      // The url query ?show=notifications is not yet used so this won't run
      showValue === 'notifications' &&
      notificationsRef &&
      notificationsRef.current
    ) {
      notificationsRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }, [])

  const { fullOnboardingSections, setFullOnboardingSections } = useOnboarding()

  useEffect(() => {
    if (fullOnboardingSections) {
      const copy = [...fullOnboardingSections]
      const index = copy.findIndex(
        (section) => section.onboardingSectionID === 'completeProfile',
      )
      if (
        copy[index] &&
        !copy[index].sectionCompleted &&
        email !== '' &&
        firstName !== '' &&
        lastName !== '' &&
        userName !== '' &&
        publicJobTitle !== ''
      ) {
        copy[index].sectionCompleted = true
        setFullOnboardingSections(copy)
      }
    }

    if (currentUser && currentUser.acceptedMarketing) {
      setAcceptedMarketing(JSON.parse(currentUser.acceptedMarketing))
    }

    if (currentUser && currentUser.preferredHomepage) {
      setCheckedHomepage(currentUser.preferredHomepage)
    }

    if (Array.isArray(error) && error.indexOf('preferredHomepage') !== -1) {
      setHomepageError(true)
      window.setTimeout(() => {
        setHomepageError(false)
      }, 2000)
    }
  }, [currentUser, error])

  useEffect(() => {
    if (selected) {
      setAvailableModules({
        audit: selected.auditAvailable,
        explain: selected.explainAvailable,
        report: selected.reportAvailable,
        track: selected.trackAvailable,
      })
    }
  }, [selected])

  const homepageLinks = ['welcome', 'track', 'report', 'explain', 'audit']

  return (
    <div className={className}>
      <h3>Homepage</h3>
      <p className={styles.byline}>Change the page you see after you login.</p>
      <div className={styles.subsection}>
        <div className={styles.updateHomepageWrapper}>
          {homepageLinks.map((page) => {
            return (
              (page === 'welcome' || availableModules[page]) && (
                <Input
                  key={page}
                  className={classNames({
                    [styles.checked]: checkedHomepage === page,
                  })}
                  name={`select-homepage-${page}`}
                  id={`select-homepage-${page}`}
                  label={
                    <>
                      <div
                        className={styles.navIcon}
                        style={{
                          color:
                            checkedHomepage === page ? '#fff' : colors[page],
                          fill:
                            checkedHomepage === page ? '#fff' : colors[page],
                        }}
                        dangerouslySetInnerHTML={{
                          __html: icons[page],
                        }}
                      />
                      <span
                        className={styles.pageName}
                      >{`${page[0].toUpperCase()}${page.substring(1)}`}</span>
                    </>
                  }
                  type="radio"
                  value="/track"
                  checked={checkedHomepage === page}
                  onClick={(e): any => {
                    e.preventDefault()
                    e.stopPropagation()

                    if (checkedHomepage !== page) {
                      dispatch(updatePreferredHomepage(page))
                    }
                  }}
                />
              )
            )
          })}
        </div>
        {homepageError && (
          <p className={styles.errorMessage}>
            Could not update. Please try again later
          </p>
        )}
      </div>

      <span className={styles.spacer} ref={notificationsRef} />

      <h3>Email notifications</h3>
      <p className={styles.byline}>
        Change which emails you receive from Uplifter.
      </p>
      <div className={styles.subsection}>
        <div className={styles.box}>
          <Row className={styles.notifs}>
            <Slot>
              <h3>Product updates</h3>
              <p>
                <strong>
                  {acceptedMarketing ? "You'll receive" : "You won't receive"}
                </strong>{' '}
                an email explaining new features and tips.
              </p>
            </Slot>
            <Slot width={100} align="center" vAlign="center">
              <button
                className={classNames({
                  [styles.enabled]: acceptedMarketing,
                  [styles.disabled]: !acceptedMarketing,
                })}
                onClick={() => {
                  const allow = !acceptedMarketing
                  dispatch(
                    updateMarketingPermissionGQL({
                      allowMarketing: allow,
                    }),
                  )
                  const actionData = {
                    action: allow ? 'allow-marketing' : 'deny-marketing',
                    longDescription: '',
                    websiteSection: 'settings',
                  }
                  dispatch(logActionAsync(actionData))
                }}
              >
                {acceptedMarketing ? 'Enabled' : 'Disabled'}
              </button>
            </Slot>
          </Row>

          <Row className={styles.notifs} odd>
            <Slot>
              <h3>Explain alerts</h3>
              <p>
                <strong>
                  {emailSettings?.emailStatus?.explainEmail
                    ? "You'll receive"
                    : "You won't receive"}
                </strong>{' '}
                updates about new explain results.
              </p>
            </Slot>
            <Slot width={100} align="center" vAlign="center">
              <button
                className={
                  emailSettings?.emailStatus?.explainEmail
                    ? styles.enabled
                    : styles.disabled
                }
                onClick={async () => {
                  await toggleEmailNotifications({
                    module: 'explain',
                    accID: accountID,
                  })
                  refetch()
                }}
              >
                {emailSettings?.emailStatus?.explainEmail
                  ? 'Enabled'
                  : 'Disabled'}
              </button>
            </Slot>
          </Row>

          {(currentAccountPermission === 'admin' ||
            currentAccountPermission === 'support') && (
            <Row className={styles.notifs}>
              <Slot padding="0 16px 0 0">
                <h3>Track requests</h3>
                <p>
                  <strong>
                    {emailSettings?.emailStatus?.trackEmail
                      ? "You'll receive"
                      : "You won't receive"}
                  </strong>{' '}
                  new requests from teammates to add new values to your campaign
                  code creator.
                </p>
              </Slot>
              <Slot width={100} align="center" vAlign="center">
                <button
                  className={
                    emailSettings?.emailStatus?.trackEmail
                      ? styles.enabled
                      : styles.disabled
                  }
                  onClick={async () => {
                    await toggleEmailNotifications({
                      module: 'track',
                      accID: accountID,
                    })
                    refetch()
                  }}
                >
                  {emailSettings?.emailStatus?.trackEmail
                    ? 'Enabled'
                    : 'Disabled'}
                </button>
              </Slot>
            </Row>
          )}
        </div>
      </div>
    </div>
  )
}

// <Row className={styles.notifs}>
//   <Slot>
//     <h3>Audit Alerts</h3>
//     <p>
//       <strong>You’ll receive</strong> updates about new audit results.
//     </p>
//   </Slot>
//   <Slot width={100} align="center" vAlign="center">
//     <button
//       className={
//         emailSettings?.auditEmail ? styles.enabled : styles.disabled
//       }
//       onClick={async () => {
//         await toggleEmailNotifications({
//           module: 'audit',
//           accID: details.currentAccount,
//         })
//         refetch()
//       }}
//     >
//       {emailSettings?.auditEmail ? 'Enabled' : 'Disabled'}
//     </button>
//   </Slot>
// </Row>
// <Row className={styles.notifs}>
//   <Slot>
//     <h3>Report Alerts</h3>
//     <p>
//       <strong>You’ll receive</strong> updates about new report
//       results.
//     </p>
//   </Slot>
//   <Slot width={100} align="center" vAlign="center">
//     <button
//       className={
//         emailSettings?.reportEmail ? styles.enabled : styles.disabled
//       }
//       onClick={async () => {
//         await toggleEmailNotifications({
//           module: 'report',
//           accID: details.currentAccount,
//         })
//         refetch()
//       }}
//     >
//       {emailSettings?.reportEmail ? 'Enabled' : 'Disabled'}
//     </button>
//   </Slot>
// </Row>

interface ProfileSettingsProps {
  className?: string
}

export default function ProfileSettings({ className }: ProfileSettingsProps) {
  const [passwordHelp, setPasswordHelp] = useState('')

  const { graphql }: { graphql: GraphQLState } = useSelector((s: any): any => s)

  const dispatch = useDispatch()
  const { currentUser } = graphql

  const {
    email,
    telNum,
    firstName,
    lastName,
    userName,
    publicJobTitle,
    profileImgLink,
  } = currentUser || {}

  useEffect(() => {
    dispatch(getCampaignLinkDashboardMetaDefaultsGQL())
  }, [performance])

  const callUpdate = () => {
    dispatch(getSelectedAccountAsync())
    dispatch(getUserListAsync())
    dispatch(getUserAccountDetailsGQL())
  }

  return (
    <div className={className}>
      <h3>Your contact information</h3>
      <p className={styles.byline}>
        Change the information Uplifter can contact you on for support and
        services.
      </p>
      <div className={styles.subsection}>
        <div className={styles.fieldRow}>
          <ChangeInput
            onClick={async (nextFirstname) => {
              if (nextFirstname === '') return 'Not valid'
              const res = await updateUserName({ firstname: nextFirstname })
              if (res) {
                callUpdate()
                return 'Updated'
              }
              return 'Failed'
            }}
            label="First name"
            type="text"
            name="firstName"
            initialValue={firstName || ''}
          />
          <ChangeInput
            onClick={async (nextLastname) => {
              if (nextLastname === '') return 'Not valid'
              const res = await updateUserName({ lastname: nextLastname })
              if (res) {
                callUpdate()
                return 'Updated'
              }
              return 'Failed'
            }}
            label="Last name"
            type="text"
            name="lastName"
            initialValue={lastName || ''}
          />
        </div>
        <div className={styles.fieldRow}>
          <ChangeInput
            onClick={async (nextEmail) => {
              const response = validateEmail(nextEmail)

              if (!response) return 'Not valid'
              const res = await updateUserEmail({ email: nextEmail })

              if (res.success) {
                callUpdate()
                return 'Updated'
              }

              if (res.error === 'IN_USE') {
                return 'Already in use'
              }

              return 'Failed'
            }}
            label="Email"
            type="email"
            name="email"
            initialValue={email || ''}
          />
          <ChangeInput
            onClick={async (nextTelNum) => {
              const res = await updateUserProfile({ telNum: nextTelNum })
              if (res) {
                callUpdate()
                return 'Updated'
              }
              return 'Failed'
            }}
            label="Phone number"
            type="text"
            name="phone"
            initialValue={`${telNum}` || ''}
          />
        </div>
      </div>

      <span className={styles.spacer} />

      <h3>Your public information</h3>
      <p className={styles.byline}>
        Change the public information available to other Uplifter users.
      </p>
      <div className={styles.subsection}>
        <div className={styles.fieldRow}>
          <ChangeInput
            onClick={async (nextUserName) => {
              const res = await updateUserProfile({ userName: nextUserName })
              if (res) {
                callUpdate()
                return 'Updated'
              }
              return 'Failed'
            }}
            label="Username"
            type="text"
            name="username"
            initialValue={userName || ''}
          />

          <ChangeInput
            onClick={async (nextPublicJobTitle) => {
              const res = await updateUserProfile({
                publicJobTitle: nextPublicJobTitle,
              })
              if (res) {
                callUpdate()
                return 'Updated'
              }
              return 'Failed'
            }}
            label="Job title"
            type="text"
            name="jobTitle"
            initialValue={publicJobTitle || ''}
          />
        </div>
        <div>
          <Label id="logo" small>
            Profile picture
          </Label>
          <ImgUploader
            logoLink={profileImgLink || ''}
            imgType="profileImg"
            uploadFn={async (file: File) => {
              const res = await updateUserImage({ file })
              if (res) {
                callUpdate()
                return true
              }
              return false
            }}
          />
        </div>
      </div>

      <span className={styles.spacer} />

      <h3>Change password</h3>
      <div className={styles.subsection}>
        {passwordHelp && (
          <p style={{ color: '#D4366A' }}>
            <strong>{passwordHelp}</strong>
          </p>
        )}
        <div className={styles.fieldRow}>
          <ChangeInput
            onClick={async (password) => {
              const response = validatePassword(password)
              setPasswordHelp(response)
              if (response) return 'Change'

              const res = await updatePassword({ password })
              if (res) {
                callUpdate()
                return 'Updated'
              }
              return 'Failed'
            }}
            label="New password"
            autoComplete="new-password"
            type="password"
            name="password"
            initialValue=""
          />
        </div>
      </div>
    </div>
  )
}
